import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Button, Space, Modal, Form, Input, Select, InputNumber, DatePicker, App, Tag, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { get, post, put, del } from '../../api/client';

interface Coupon {
  id: string;
  code: string;
  name: string;
  discountType: string;
  discountValue: number;
  minAmount: number | null;
  maxUses: number;
  usedCount: number;
  isActive: boolean;
  expiresAt: string | null;
}

export default function DiscountCouponsPage() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const { data, isLoading } = useQuery({
    queryKey: ['discount-coupons'],
    queryFn: () => get<Coupon[]>('/admin/discount-coupons'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['discount-coupons'] });

  const save = useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Record<string, unknown> }) =>
      id ? put(`/admin/discount-coupons/${id}`, values) : post('/admin/discount-coupons', values),
    onSuccess: () => {
      message.success('优惠券已保存');
      setEditing(null);
      setCreating(false);
      void invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '保存失败'),
  });

  const openEdit = (coupon: Coupon) => {
    setEditing(coupon);
    form.setFieldsValue({
      ...coupon,
      expiresAt: coupon.expiresAt ? dayjs(coupon.expiresAt) : null,
    });
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      save.mutate({
        id: editing?.id,
        values: {
          code: values.code || undefined,
          name: values.name,
          discountType: values.discountType,
          discountValue: values.discountValue,
          minAmount: values.minAmount ?? null,
          maxUses: values.maxUses ?? 0,
          isActive: values.isActive ?? true,
          expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
        },
      });
    } catch {
      /* 校验失败 */
    }
  };

  return (
    <Card
      title="优惠券管理"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setCreating(true); form.resetFields(); }}>
          新建优惠券
        </Button>
      }
    >
      <Table<Coupon>
        rowKey="id"
        loading={isLoading}
        dataSource={data ?? []}
        pagination={false}
        scroll={{ x: 1030 }}
        columns={[
          { title: '券码', dataIndex: 'code', width: 120, render: (v: string) => <Tag color="cyan">{v}</Tag> },
          { title: '名称', dataIndex: 'name', width: 180 },
          {
            title: '优惠',
            key: 'discount',
            width: 140,
            render: (_, r) =>
              r.discountType === 'percentage' ? (
                <span>{r.discountValue}% 折扣</span>
              ) : (
                <span>减 ¥{r.discountValue}</span>
              ),
          },
          { title: '门槛', dataIndex: 'minAmount', width: 100, render: (v: number | null) => (v ? `满 ¥${v}` : '无') },
          {
            title: '使用 / 上限',
            key: 'usage',
            width: 120,
            render: (_, r) => `${r.usedCount} / ${r.maxUses > 0 ? r.maxUses : '∞'}`,
          },
          {
            title: '有效期',
            dataIndex: 'expiresAt',
            width: 120,
            render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD') : '长期'),
          },
          {
            title: '状态',
            dataIndex: 'isActive',
            width: 90,
            render: (v: boolean) => (v ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>),
          },
          {
            title: '操作',
            key: 'action',
            width: 160,
            fixed: 'right',
            render: (_, r) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
                  编辑
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    modal.confirm({
                      title: '删除优惠券',
                      content: `确认删除「${r.name}」？`,
                      okButtonProps: { danger: true },
                      onOk: async () => {
                        await del(`/admin/discount-coupons/${r.id}`);
                        message.success('删除成功');
                        void invalidate();
                      },
                    })
                  }
                >
                  删除
                </Button>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editing ? '编辑优惠券' : '新建优惠券'}
        open={creating || editing !== null}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={submit}
        confirmLoading={save.isPending}
      >
        <Form form={form} layout="vertical">
          {!editing && (
            <Form.Item name="code" label="券码（留空自动生成）">
              <Input placeholder="留空自动生成" />
            </Form.Item>
          )}
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="discountType" label="优惠类型" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'fixed', label: '固定金额' },
                { value: 'percentage', label: '百分比' },
              ]}
            />
          </Form.Item>
          <Form.Item name="discountValue" label="优惠值" rules={[{ required: true, message: '请输入优惠值' }]}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="minAmount" label="使用门槛金额">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="maxUses" label="使用上限（0 = 不限）">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="expiresAt" label="有效期至">
            <DatePicker style={{ width: '100%' }} showTime />
          </Form.Item>
          <Form.Item name="isActive" label="是否启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}